"use client";

import { type ReactNode } from "react";
import { X } from "lucide-react";
import { Button } from "./button";

type OnboardingNoticeCardProps = {
    badge: string;
    titleLines: string[];
    description: string;
    bullets: string[];
    icon: ReactNode;
    secondaryLabel: string;
    dismissLabel: string;
    onDismiss: () => void;
    primaryAction: ReactNode;
};

export function OnboardingNoticeCard({
    badge,
    titleLines,
    description,
    bullets,
    icon,
    secondaryLabel,
    dismissLabel,
    onDismiss,
    primaryAction,
}: OnboardingNoticeCardProps) {
    return (
        <div className="relative overflow-hidden rounded-2xl border border-cyan-700/40 bg-linear-to-br from-slate-900/90 to-slate-800/90 shadow-[0_0_32px_-6px_rgba(34,211,238,0.22)] backdrop-blur-md">
            <div
                className="pointer-events-none absolute inset-x-0 top-0 h-px bg-linear-to-r from-transparent via-cyan-400/70 to-transparent"
                aria-hidden="true"
            />
            <div
                className="pointer-events-none absolute -right-16 -top-16 h-40 w-40 rounded-full bg-cyan-500/10 blur-3xl"
                aria-hidden="true"
            />
            <div
                className="pointer-events-none absolute -bottom-20 -left-16 h-44 w-44 rounded-full bg-blue-600/10 blur-3xl"
                aria-hidden="true"
            />

            <div className="relative space-y-5 p-6 sm:p-7">
                <div className="flex items-start justify-between gap-4">
                    <span className="inline-flex items-center rounded-full border border-cyan-500/40 bg-cyan-500/15 px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-[0.18em] text-cyan-200">
                        {badge}
                    </span>
                    <button
                        type="button"
                        onClick={onDismiss}
                        className="-mr-1 -mt-1 rounded-full p-2 text-slate-400 transition hover:bg-slate-800/70 hover:text-white"
                        aria-label={dismissLabel}
                    >
                        <X className="h-4 w-4" />
                    </button>
                </div>

                <div className="flex items-start gap-5">
                    <div className="mt-0.5 shrink-0 rounded-xl border border-cyan-500/20 bg-cyan-500/10 p-3 text-cyan-400">
                        {icon}
                    </div>
                    <div className="min-w-0 space-y-3">
                        <h2 className="text-xl font-bold leading-snug text-white">
                            {titleLines.map((line, index) => (
                                <span key={`${line}-${index}`}>
                                    {line}
                                    {index < titleLines.length - 1 ? <br /> : null}
                                </span>
                            ))}
                        </h2>
                        <p className="text-sm leading-relaxed text-gray-300">{description}</p>
                        <ul className="space-y-1.5 text-sm text-gray-400">
                            {bullets.map((bullet) => (
                                <li key={bullet} className="flex gap-2">
                                    <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-cyan-400/80" aria-hidden="true" />
                                    <span>{bullet}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>

                <div className="flex flex-col-reverse gap-3 border-t border-slate-700/60 pt-5 sm:flex-row sm:items-center sm:justify-end">
                    <Button
                        variant="ghost"
                        className="text-slate-300 hover:bg-slate-800/80 hover:text-white"
                        onClick={onDismiss}
                    >
                        {secondaryLabel}
                    </Button>
                    {primaryAction}
                </div>
            </div>
        </div>
    );
}
